/**
 * Centralized Constants for AppBouncer SDK
 *
 * This module exports all magic numbers and configuration values
 * used throughout the SDK. Importing from here ensures consistent
 * values and easier calibration.
 *
 * Feature-specific configurations are located in their respective
 * feature folders (e.g., liveness/config.ts) and re-exported here.
 */

// ============================================================
// Camera Constants
// ============================================================

export const CAMERA = {
  /** Default video width for desktop */
  DEFAULT_WIDTH_DESKTOP: 1280,
  /** Default video height for desktop */
  DEFAULT_HEIGHT_DESKTOP: 720,
  /** Default video width for mobile */
  DEFAULT_WIDTH_MOBILE: 480,
  /** Default video height for mobile */
  DEFAULT_HEIGHT_MOBILE: 640,
  /** Min video width */
  MIN_WIDTH: 640,
  /** Max video width */
  MAX_WIDTH: 1920,
  /** Min video height */
  MIN_HEIGHT: 480,
  /** Max video height */
  MAX_HEIGHT: 1080,
  /** Video metadata load timeout in ms */
  METADATA_TIMEOUT_MS: 10000,
  /** Default image capture quality (0-1) */
  CAPTURE_QUALITY: 0.9,
} as const;

// ============================================================
// Polling Constants
// ============================================================

export const POLLING = {
  /** Initial polling interval in ms */
  INITIAL_INTERVAL_MS: 1000,
  /** Maximum polling interval in ms */
  MAX_INTERVAL_MS: 30000,
  /** Backoff multiplier for exponential backoff */
  BACKOFF_FACTOR: 1.5,
  /** Maximum number of polling attempts */
  MAX_ATTEMPTS: 60,
  /** Jitter factor for randomizing intervals (0-1) */
  JITTER_FACTOR: 0.2,
  /** Default polling request timeout in ms */
  REQUEST_TIMEOUT_MS: 10000,
} as const;

// ============================================================
// Age Estimation Constants (Bucket-based Estimator)
// ============================================================

export const AGE_ESTIMATION = {
  /** Number of estimates to average for multi-estimate */
  DEFAULT_ESTIMATE_COUNT: 8,
  /** Delay between estimates in ms */
  ESTIMATE_DELAY_MS: 500,
  /** Minimum confidence threshold for valid estimate */
  MIN_CONFIDENCE: 0.5,
  /** Default embedding dimension */
  EMBEDDING_DIM: 512,
  /** Face input size for ONNX model */
  FACE_INPUT_SIZE: 112,
} as const;

// ============================================================
// Age Verifier Constants (Threshold-based Verifier)
// ============================================================

/**
 * Model presets for the Age Verifier.
 *
 * Each preset defines the model file, input dimensions, and normalization
 * parameters for a specific trained model. This allows easy switching
 * between models without modifying code.
 */
export const AGE_VERIFIER_MODELS = {
  /**
   * EfficientNet-Lite0 model (2025-01) - RECOMMENDED
   * Smaller and faster with 224×224 input
   */
  EFFICIENTNET_LITE0_FP16: {
    modelPath: './models/18_fp16.onnx',
    inputSize: 224,
    inputName: 'image',
    outputName: 'probabilities',
  },
  /**
   * EfficientNet-Lite0 INT8 quantized model
   * Smallest size, best for mobile devices
   */
  EFFICIENTNET_LITE0_INT8: {
    modelPath: './models/18_int8.onnx',
    inputSize: 224,
    inputName: 'image',
    outputName: 'probabilities',
  },
  /**
   * Legacy EfficientNet-B2 model (2024)
   * Larger model with 260×260 input
   */
  EFFICIENTNET_B2_FP16: {
    modelPath: './models/model_fp16.onnx',
    inputSize: 260,
    inputName: 'image',
    outputName: 'probabilities',
  },
} as const;

export const AGE_VERIFIER = {
  /** Default model preset to use */
  DEFAULT_MODEL: 'EFFICIENTNET_LITE0_FP16' as keyof typeof AGE_VERIFIER_MODELS,

  /** Age thresholds supported by the model (12+, 15+, 18+, 21+, 25+) */
  THRESHOLDS: [12, 15, 18, 21, 25] as const,

  /** Default risk tolerance for verification (2% = need 98% confidence) */
  DEFAULT_RISK_TOLERANCE: 0.02,

  /** Default execution provider */
  DEFAULT_EXECUTION_PROVIDER: 'wasm' as const,

  /**
   * ImageNet normalization constants (used by all models)
   * These are fixed values from ImageNet dataset statistics
   */
  IMAGENET_NORMALIZATION: {
    MEAN: [0.485, 0.456, 0.406] as const,
    STD: [0.229, 0.224, 0.225] as const,
  },

  /** Face alignment parameters */
  FACE_ALIGNMENT: {
    /** Intermediate crop size before resize to model input */
    CROP_SIZE: 200,
    /** Eye distance as percentage of crop width (0.4 = 40%) */
    EYE_DISTANCE_RATIO: 0.4,
  },

  /** Multi-verification settings */
  MULTI_VERIFY: {
    /** Default number of verification runs */
    DEFAULT_COUNT: 4,
    /** Delay between verifications in ms */
    DELAY_MS: 600,
    /** Minimum confidence to include in results */
    MIN_CONFIDENCE: 0.5,
    /** Countdown seconds before starting live capture */
    COUNTDOWN_SECONDS: 3,
    /** Minimum pass rate for overall verification (80%) */
    MIN_PASS_RATE: 80,
  },

  /** MediaPipe landmark indices for eye detection */
  LANDMARK_INDICES: {
    LEFT_EYE: [33, 133, 159, 145, 153, 144] as const,
    RIGHT_EYE: [362, 263, 386, 374, 380, 373] as const,
  },
} as const;

// ============================================================
// Face Detection Constants
// ============================================================

export const FACE_DETECTION = {
  /** Minimum face size ratio (face width / video width) */
  MIN_FACE_RATIO: 0.15,
  /** Maximum face size ratio */
  MAX_FACE_RATIO: 0.85,
  /** Face center tolerance (distance from center / video dimension) */
  CENTER_TOLERANCE: 0.25,
  /** Minimum detection confidence */
  MIN_CONFIDENCE: 0.8,
  /** Face detection loop interval in ms */
  LOOP_INTERVAL_MS: 100,
} as const;

// ============================================================
// Document Capture Constants
// ============================================================

export const DOCUMENT = {
  /** Standard ID card aspect ratio (width / height) */
  ID_CARD_ASPECT_RATIO: 1.586,
  /** Passport aspect ratio */
  PASSPORT_ASPECT_RATIO: 1.42,
  /** Driver's license aspect ratio */
  DRIVERS_LICENSE_ASPECT_RATIO: 1.586,
  /** Minimum document quality score (0-1) */
  MIN_QUALITY_SCORE: 0.7,
  /** Blur detection threshold */
  BLUR_THRESHOLD: 100,
  /** Minimum brightness (0-255) */
  MIN_BRIGHTNESS: 50,
  /** Maximum brightness (0-255) */
  MAX_BRIGHTNESS: 230,
  /** Glare detection threshold */
  GLARE_THRESHOLD: 245,
} as const;

// ============================================================
// Network/API Constants
// ============================================================

export const NETWORK = {
  /** Default request timeout in ms */
  DEFAULT_TIMEOUT_MS: 30000,
  /** Requirements check timeout in ms */
  REQUIREMENTS_TIMEOUT_MS: 10000,
  /** Upload timeout in ms */
  UPLOAD_TIMEOUT_MS: 60000,
  /** Maximum retry attempts for network errors */
  MAX_RETRIES: 3,
  /** Retry delay in ms */
  RETRY_DELAY_MS: 1000,
} as const;

// ============================================================
// UI/Animation Constants
// ============================================================

export const UI = {
  /** Screen transition duration in ms */
  TRANSITION_DURATION_MS: 300,
  /** Progress bar animation duration in ms */
  PROGRESS_ANIMATION_MS: 200,
  /** Toast notification duration in ms */
  TOAST_DURATION_MS: 3000,
  /** Debounce delay for face detection callbacks in ms */
  FACE_CALLBACK_DEBOUNCE_MS: 100,
} as const;

// ============================================================
// Session Constants
// ============================================================

export const SESSION = {
  /** Session ID prefix */
  ID_PREFIX: 'sess_',
  /** Session expiry buffer in ms (check before actual expiry) */
  EXPIRY_BUFFER_MS: 60000,
  /** Maximum session duration in ms (5 minutes) */
  MAX_DURATION_MS: 300000,
} as const;

// ============================================================
// Type Exports
// ============================================================

export type CameraConstants = typeof CAMERA;
export type PollingConstants = typeof POLLING;
export type AgeEstimationConstants = typeof AGE_ESTIMATION;
export type AgeVerifierConstants = typeof AGE_VERIFIER;
export type AgeVerifierModelPreset = keyof typeof AGE_VERIFIER_MODELS;
export type AgeVerifierModelConfig = (typeof AGE_VERIFIER_MODELS)[AgeVerifierModelPreset];
export type FaceDetectionConstants = typeof FACE_DETECTION;
export type DocumentConstants = typeof DOCUMENT;
export type NetworkConstants = typeof NETWORK;
export type UIConstants = typeof UI;
export type SessionConstants = typeof SESSION;
